import { useFormContext, useWatch } from 'react-hook-form';
import type { FieldArrayWithId } from 'react-hook-form';

import styled from 'styled-components';
import { FaTrash } from 'react-icons/fa';

import { colors } from 'constants/colors';
import { ErrorType, FORM_ERRORS, MIN } from '../../constants/formErrors';

import { ProductPriceRangeType, SelectedProductArrangement } from './ProductPriceRangeCreate';
import { renderAsPrice } from 'helpers/price';
import { TotemLabel } from 'components/TotemLabel';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';
import { DetailLink } from 'components/DetailsView/DetailLink';

export type FieldErrorData = {
    index: number;
    fieldName: string;
    fieldTitle: string;
};

type ProductArrangementFields = FieldArrayWithId<ProductPriceRangeType, 'productArrangements', 'id'>[];

export const ProductArrangementsPriceTable = ({
    selectedProductArrangements,
    fields,
    removeProductArrangement,
}: {
    selectedProductArrangements: SelectedProductArrangement[];
    fields: ProductArrangementFields;
    removeProductArrangement: (index: number) => void;
}) => {
    const {
        register,
        formState: { errors },
    } = useFormContext();

    const watchedProductArrangements = useWatch({ name: 'productArrangements', defaultValue: fields });

    return (
        <Container>
            <TotemLabel>Gamme de produits dans la gamme de prix</TotemLabel>
            <ProductsTable>
                <thead>
                    <tr>
                        <ProductsTableHeaderCell>Gamme de produits</ProductsTableHeaderCell>
                        <ProductsTableHeaderCell $isDataCentered>Prix de vente HT</ProductsTableHeaderCell>
                        <ProductsTableHeaderCell $isDataCentered>Prix barré HT</ProductsTableHeaderCell>
                        <ProductsTableHeaderCell $isDataCentered>Supprimer</ProductsTableHeaderCell>
                    </tr>
                </thead>
                <ProductsTableBody>
                    {fields.map((item, index) => {
                        const { id: fieldId, productArrangementId, price, crossedPrice } = item;
                        const name =
                            selectedProductArrangements?.find(
                                ({ productArrangementId: selectedProductArrangementId }) =>
                                    selectedProductArrangementId === productArrangementId,
                            )?.name || '';

                        // @ts-expect-error don't have time
                        const errorType: ErrorType | undefined = errors?.productArrangements?.[index]?.price?.type;
                        const numberValue = errorType === MIN ? '0' : '';
                        const watchedPrice = watchedProductArrangements[index]?.price;

                        return (
                            <tr key={fieldId}>
                                <ProductsTableBodyCell>
                                    <DetailLink name={name} value={productArrangementId} path="product-arrangement" />
                                </ProductsTableBodyCell>
                                <PriceTableBodyCell>
                                    <QuantityInputInnerContainer>
                                        <DetailFormValue
                                            placeholder="?"
                                            defaultValue={price}
                                            type="number"
                                            step=".0001"
                                            width="100%"
                                            $centerText
                                            customError={errorType && FORM_ERRORS[errorType] + numberValue}
                                            {...register(`productArrangements.${index}.price`, {
                                                min: 0,
                                                required: true,
                                                valueAsNumber: true,
                                            })}
                                        />
                                    </QuantityInputInnerContainer>
                                </PriceTableBodyCell>
                                <PriceTableBodyCell>
                                    <QuantityInputInnerContainer>
                                        <DetailFormValue
                                            placeholder="?"
                                            defaultValue={crossedPrice}
                                            type="number"
                                            step=".0001"
                                            width="100%"
                                            $centerText
                                            customError={errorType && FORM_ERRORS[errorType] + numberValue}
                                            {...register(`productArrangements.${index}.crossedPrice`, {
                                                required: false,
                                                setValueAs: (value) => (!value ? null : Number(value)),
                                                validate: (crossedPrice) =>
                                                    crossedPrice === 0 || (crossedPrice && !Number.isNaN(crossedPrice))
                                                        ? crossedPrice > watchedPrice ||
                                                          `Doit être > ${renderAsPrice(watchedPrice)}`
                                                        : true,
                                            })}
                                        />
                                    </QuantityInputInnerContainer>
                                </PriceTableBodyCell>
                                <ProductsTableBodyCell $isDataCentered>
                                    <DeleteIconContainer onClick={() => removeProductArrangement(index)}>
                                        <FaTrash data-test="trash-icon" size={20} color={colors.red} />
                                    </DeleteIconContainer>
                                </ProductsTableBodyCell>
                            </tr>
                        );
                    })}
                </ProductsTableBody>
            </ProductsTable>
        </Container>
    );
};

const PriceTableBodyCell = styled.td`
    display: table-cell;
    text-align: -webkit-center;
    padding: 5px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    width: 100%;
    overflow-y: scroll;
    min-height: 200px;

    & > :not(:first-child) {
        margin-top: 5px;
    }
`;

const ProductsTable = styled.table`
    border-radius: ${({ theme }) => theme.borderRadius};
    border-spacing: 0px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    > td {
        padding-top: 5px;
    }
`;

const ProductsTableBody = styled.tbody`
    & > :not(:first-child) td {
        border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
`;

const ProductsTableHeaderCell = styled.th<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.textColor};
`;

const ProductsTableBodyCell = styled.td<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
    color: ${({ theme }) => theme.textColor};
`;

const QuantityInputInnerContainer = styled.div`
    width: 80px;
`;

const DeleteIconContainer = styled.div`
    display: inline;
    padding: 5px;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;
